/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { X, Plus, Send, CheckCircle, Circle, ClipboardList } from 'lucide-react';
import { C, inp, lbl, fmt } from './teamUtils';

const EVAL_SECTIONS = {
  FOH: [
    { key: 'general', label: 'General Standards', items: [
      { key: 'time_keeping',        label: 'Time & Attendance' },
      { key: 'checklist_completion',label: 'Checklist / Task Completion' },
      { key: 'house_rules',         label: 'House Rules & Breaks' },
      { key: 'dress_code',          label: 'Dress Code' },
    ]},
    { key: 'attitude', label: 'Attitude & Team', items: [
      { key: 'commitment',          label: 'Commitment & Motivation' },
      { key: 'feedback_collegiality',label: 'Feedback / Collegiality' },
      { key: 'guest_hospitality',   label: 'Guest Interaction & Hospitality' },
    ]},
    { key: 'service', label: 'Service', items: [
      { key: 'sequence_of_service', label: 'Sequence of Service' },
      { key: 'food_running_clearing',label: 'Food Running & Table Clearing' },
      { key: 'menu_knowledge',      label: 'Food Menu Knowledge' },
      { key: 'efficiency_multitasking', label: 'Efficiency & Multitasking' },
    ]},
    { key: 'bar', label: 'Bar', items: [
      { key: 'coffee_soft_drinks',  label: 'Coffee & Soft Drinks' },
      { key: 'wine_knowledge',      label: 'Wine Knowledge & Descriptions' },
      { key: 'wine_service',        label: 'Wine Service' },
      { key: 'cocktail_knowledge',  label: 'Cocktail Knowledge' },
      { key: 'bartending_barista',  label: 'Bartending / Barista Skills' },
    ]},
  ],
  KIT: [
    { key: 'general', label: 'General Standards', items: [
      { key: 'time_keeping',        label: 'Time & Attendance' },
      { key: 'house_rules',         label: 'House Rules & Breaks' },
      { key: 'dress_code',          label: 'Dress Code & Hygiene' },
    ]},
    { key: 'attitude', label: 'Attitude & Team', items: [
      { key: 'commitment',          label: 'Commitment & Motivation' },
      { key: 'feedback_collegiality',label: 'Feedback / Collegiality' },
      { key: 'communication_foh',   label: 'Communication with FOH' },
    ]},
    { key: 'kitchen', label: 'Kitchen Performance', items: [
      { key: 'station_management',  label: 'Station Management' },
      { key: 'prep_standards',      label: 'Prep Standards & Quality' },
      { key: 'hygiene_cleanliness', label: 'Hygiene & Cleanliness' },
      { key: 'speed_efficiency',    label: 'Speed & Efficiency' },
      { key: 'knife_skills',        label: 'Knife Skills' },
      { key: 'recipe_consistency',  label: 'Recipe Consistency' },
    ]},
  ],
};

const SCORE_LABELS = {
  1: { label: 'Needs Improvement', color: C.red,    bg: C.redLight    },
  2: { label: 'Developing',        color: C.amber,  bg: C.amberLight  },
  3: { label: 'Meets Expectations',color: C.skyText,bg: C.skyBlueLight},
  4: { label: 'Exceeds Expectations',color:C.green, bg: C.greenLight  },
};

function ScoreButton({ score, selected, onSelect }) {
  const s = SCORE_LABELS[score];
  const active = selected === score;
  return (
    <button onClick={() => onSelect(score)}
      style={{
        flex: 1, padding: '6px 4px', border: `1.5px solid ${active ? s.color : C.border}`,
        borderRadius: '8px', cursor: 'pointer', fontSize: '11px', fontWeight: active ? 700 : 400,
        background: active ? s.bg : C.white, color: active ? s.color : C.mid,
        transition: 'all 0.15s', textAlign: 'center', lineHeight: 1.3,
      }}>
      <div style={{ fontSize: '14px', fontWeight: 700 }}>{score}</div>
      <div style={{ fontSize: '10px' }}>{s.label}</div>
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════
// EVALUATION MODAL
// ══════════════════════════════════════════════════════════════════════
export function EvaluationModal({ member, currentUser, onClose, onSaved, evalType = 'standard' }) {
  const isMidContract = evalType === 'mid_contract';
  const [type,       setType]       = useState(null);
  const [step,       setStep]       = useState('type');
  const [saving,     setSaving]     = useState(false);
  const [saved,      setSaved]      = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  // Form fields
  const [date,            setDate]           = useState(new Date().toISOString().split('T')[0]);
  const [nextEvalDate,    setNextEvalDate]   = useState('');
  const [hkManual,        setHkManual]       = useState(false);
  const [hkSystems,       setHkSystems]      = useState(false);
  const [hkSystemNotes,   setHkSystemNotes]  = useState('');
  const [ciGoing,         setCiGoing]        = useState('');
  const [ciWell,          setCiWell]         = useState('');
  const [ciImprove,       setCiImprove]      = useState('');
  const [ciSuggestions,   setCiSuggestions]  = useState('');
  const [ratings,         setRatings]        = useState({}); // { key: { score, notes } }
  const [ambitions,       setAmbitions]      = useState('');
  const [goals,           setGoals]          = useState([
    { title: '', action: '', target_date: '' },
    { title: '', action: '', target_date: '' },
    { title: '', action: '', target_date: '' },
  ]);
  const [signedEmployee,  setSignedEmployee] = useState(false);
  const [signedManager,   setSignedManager]  = useState(false);

  function setScore(key, score) {
    setRatings(p => ({ ...p, [key]: { ...(p[key] || {}), score } }));
  }
  function setNotes(key, notes) {
    setRatings(p => ({ ...p, [key]: { ...(p[key] || {}), notes } }));
  }
  function setGoalField(i, field, val) {
    setGoals(p => p.map((g, idx) => idx === i ? { ...g, [field]: val } : g));
  }

  function allScoresFilled() {
    if (!type) return false;
    const sections = EVAL_SECTIONS[type];
    for (const section of sections) {
      for (const item of section.items) {
        if (!ratings[item.key]?.score) return false;
      }
    }
    return true;
  }

  async function handleSave() {
    if (!allScoresFilled()) { alert('Please score all evaluation areas before saving.'); return; }
    setSaving(true);
    const payload = {
      team_member_id: member.id,
      type,
      eval_type: evalType,
      evaluator: currentUser?.name || 'Manager',
      evaluator_role: currentUser?.role || '',
      date,
      next_eval_date: nextEvalDate || null,
      hk_manual_confirmed: hkManual,
      hk_no_system_issues: hkSystems,
      hk_system_notes: hkSystemNotes || null,
      ci_going: ciGoing || null,
      ci_doing_well: ciWell || null,
      ci_improve: ciImprove || null,
      ci_suggestions: ciSuggestions || null,
      ratings,
      ambitions: ambitions || null,
      goals: goals.filter(g => g.title.trim()),
      signed_employee: signedEmployee,
      signed_manager: signedManager,
    };
    const { error } = await supabase.from('evaluations').insert(payload);
    setSaving(false);
    if (error) { alert('Error saving evaluation: ' + error.message); return; }
    // For mid-contract: mark pulse as dismissed with timestamp
    if (isMidContract) {
      await supabase.from('team_members').update({
        pulse_dismissed: true,
        pulse_dismissed_at: new Date().toISOString(),
      }).eq('id', member.id);
    }
    setSaved(true);
    if (onSaved) onSaved();
  }

  async function handleSendEmail() {
    if (!member.email) { alert('No email address on file for this team member.'); return; }
    setEmailSending(true);
    const sections = EVAL_SECTIONS[type];
    const scoreLines = sections.flatMap(s =>
      s.items.map(i => `${i.label}: ${ratings[i.key]?.score ? SCORE_LABELS[ratings[i.key].score].label : '—'}${ratings[i.key]?.notes ? ` — ${ratings[i.key].notes}` : ''}`)
    ).join('\n');
    const body = `Hi ${member.name.split(' ')[0]},\n\nPlease find a summary of your probation evaluation below.\n\nDate: ${fmt(date)}\nEvaluator: ${currentUser?.name || 'Manager'}\n\n--- EVALUATION SUMMARY ---\n${scoreLines}\n\nAmbitions: ${ambitions || '—'}\n\nGoals:\n${goals.filter(g=>g.title).map((g,i)=>`${i+1}. ${g.title}${g.action ? ` — ${g.action}` : ''}${g.target_date ? ` (by ${fmt(g.target_date)})` : ''}`).join('\n') || '—'}\n\nNext evaluation: ${nextEvalDate ? fmt(nextEvalDate) : '—'}\n\nThanks,\n${currentUser?.name || 'Management'}`;
    const { error } = await supabase.functions.invoke('send-evaluation-email', {
      body: { to: member.email, name: member.name, body, sentBy: currentUser?.name || 'Manager' },
    }).catch(() => ({ error: true }));
    if (error) {
      alert('Email function not yet set up — evaluation is saved. You can copy the summary manually.');
    } else {
      await supabase.from('evaluations')
        .update({ email_sent_at: new Date().toISOString(), email_sent_to: member.email })
        .eq('team_member_id', member.id)
        .order('created_at', { ascending: false })
        .limit(1);
    }
    setEmailSending(false);
  }

  const sectionHeaderStyle = {
    fontSize: '11px', fontWeight: 700, color: C.white, textTransform: 'uppercase',
    letterSpacing: '0.06em', padding: '6px 10px', background: C.navy,
    borderRadius: '6px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px',
  };

  // ── Type picker ──
  if (step === 'type') return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:700, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'400px', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', overflow:'hidden' }}>
        <div style={{ padding:'20px 24px', background:C.navy, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'15px', color:C.white }}>{isMidContract ? 'Mid-Contract Check-in' : 'Evaluation'}</div>
            <div style={{ fontSize:'12px', color:C.skyBlue, marginTop:'2px' }}>{member.name}</div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex' }}><X size={18}/></button>
        </div>
        <div style={{ padding:'24px' }}>
          <p style={{ fontSize:'13px', color:C.mid, margin:'0 0 18px', lineHeight:1.5 }}>Select the evaluation form type:</p>
          <div style={{ display:'flex', gap:'12px' }}>
            {[['FOH', '☕', 'Front of House — service, bar, hospitality'], ['KIT', '🍳', 'Kitchen — prep, station, hygiene']].map(([t, icon, desc]) => (
              <button key={t} onClick={() => { setType(t); setStep('form'); }}
                style={{ flex:1, padding:'20px 14px', background:C.cream, border:`2px solid ${C.border}`, borderRadius:'12px', cursor:'pointer', textAlign:'center', transition:'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.navy; e.currentTarget.style.background = C.white; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.cream; }}>
                <div style={{ fontSize:'2rem', marginBottom:'8px' }}>{icon}</div>
                <div style={{ fontWeight:700, fontSize:'14px', color:C.charcoal, marginBottom:'4px' }}>{t}</div>
                <div style={{ fontSize:'11px', color:C.mid, lineHeight:1.4 }}>{desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ── Saved confirmation ──
  if (saved) return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:700, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'420px', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', overflow:'hidden' }}>
        <div style={{ padding:'20px 24px', background:C.navy, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'15px', color:C.white }}>Evaluation Saved</div>
            <div style={{ fontSize:'12px', color:C.skyBlue, marginTop:'2px' }}>{member.name} · {type}</div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex' }}><X size={18}/></button>
        </div>
        <div style={{ padding:'28px 24px', textAlign:'center' }}>
          <div style={{ fontSize:'2.5rem', marginBottom:'12px' }}>✅</div>
          <p style={{ fontWeight:700, fontSize:'15px', color:C.charcoal, margin:'0 0 6px' }}>Evaluation recorded</p>
          <p style={{ fontSize:'13px', color:C.mid, margin:'0 0 24px' }}>Saved to {member.name}'s card.</p>
          <div style={{ display:'flex', gap:'10px', justifyContent:'center', flexWrap:'wrap' }}>
            {member.email && (
              <button onClick={handleSendEmail} disabled={emailSending}
                style={{ padding:'10px 20px', background:emailSending?C.border:C.skyText, color:C.white, border:'none', borderRadius:'9px', cursor:emailSending?'not-allowed':'pointer', fontWeight:600, fontSize:'13px', display:'flex', alignItems:'center', gap:'6px' }}>
                <Send size={14}/>{emailSending ? 'Sending…' : 'Email summary to TM'}
              </button>
            )}
            <button onClick={onClose}
              style={{ padding:'10px 20px', background:C.cream, color:C.navy, border:`1px solid ${C.border}`, borderRadius:'9px', cursor:'pointer', fontWeight:600, fontSize:'13px' }}>
              Close
            </button>
          </div>
          {!member.email && <p style={{ fontSize:'11px', color:C.mid, marginTop:'12px' }}>No email on file — add one to the TM card to enable email summary.</p>}
        </div>
      </div>
    </div>
  );

  // ── Full form ──
  const sections = EVAL_SECTIONS[type] || [];
  const totalItems = sections.flatMap(s => s.items).length;
  const scoredItems = sections.flatMap(s => s.items).filter(i => ratings[i.key]?.score).length;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:700, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'680px', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', display:'flex', flexDirection:'column' }}>

        {/* Header */}
        <div style={{ padding:'18px 24px', background:C.navy, borderRadius:'16px 16px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'15px', color:C.white }}>{isMidContract ? 'Mid-Contract Check-in' : 'Evaluation'} — {type}</div>
            <div style={{ fontSize:'12px', color:C.skyBlue, marginTop:'2px' }}>{member.name} · {member.venue}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            <span style={{ fontSize:'12px', color: scoredItems === totalItems ? C.yellow : 'rgba(255,255,255,0.5)', fontWeight:600 }}>
              {scoredItems}/{totalItems} scored
            </span>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex' }}><X size={18}/></button>
          </div>
        </div>

        <div style={{ padding:'24px', display:'flex', flexDirection:'column', gap:'24px' }}>

          {/* Meta */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
            <div>
              <label style={lbl}>Evaluation Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp}/>
            </div>
            <div>
              <label style={lbl}>Next Evaluation Date</label>
              <input type="date" value={nextEvalDate} onChange={e => setNextEvalDate(e.target.value)} style={inp}/>
            </div>
            <div>
              <label style={lbl}>Evaluator</label>
              <input value={currentUser?.name || 'Manager'} readOnly style={{ ...inp, background:C.cream, color:C.mid }}/>
            </div>
            <div>
              <label style={lbl}>Type</label>
              <div style={{ display:'flex', gap:'6px', alignItems:'center', paddingTop:'8px' }}>
                <span style={{ padding:'4px 12px', background:C.navy, color:C.white, borderRadius:'20px', fontSize:'12px', fontWeight:700 }}>{type}</span>
                <button onClick={() => setStep('type')} style={{ fontSize:'11px', color:C.skyText, background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>change</button>
              </div>
            </div>
          </div>

          {/* Housekeeping */}
          <div>
            <div style={sectionHeaderStyle}><ClipboardList size={13}/>Housekeeping</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {[
                [hkManual,  setHkManual,  '📋 Employee has FOH training manual / checklist'],
                [hkSystems, setHkSystems, '💻 No issues with Eitje / Tebi / systems'],
              ].map(([val, setter, label], i) => (
                <div key={i} onClick={() => setter(!val)}
                  style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', background:val?C.greenLight:C.white, border:`1px solid ${val?C.green+'55':C.border}`, borderRadius:'8px', cursor:'pointer', transition:'all 0.15s' }}>
                  {val ? <CheckCircle size={18} color={C.green}/> : <Circle size={18} color={C.border}/>}
                  <span style={{ fontSize:'13px', color:val?C.green:C.charcoal, fontWeight:val?600:400 }}>{label}</span>
                </div>
              ))}
              <div>
                <label style={{ ...lbl, marginTop:'6px' }}>System issues / notes <span style={{ fontWeight:400, color:C.mid }}>(optional)</span></label>
                <input value={hkSystemNotes} onChange={e => setHkSystemNotes(e.target.value)} placeholder="e.g. Had trouble clocking in via Eitje" style={inp}/>
              </div>
            </div>
          </div>

          {/* Check-in */}
          <div>
            <div style={sectionHeaderStyle}>💬 Check-in — Employee Self-Assessment</div>
            <div style={{ fontSize:'11px', color:C.mid, marginBottom:'12px', fontStyle:'italic' }}>Fill in together during the conversation. These are the employee's own words.</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              {[
                [ciGoing,       setCiGoing,       'How do you think things are going overall?'],
                [ciWell,        setCiWell,        'What do you think you are doing well at?'],
                [ciImprove,     setCiImprove,     'What do you think you could improve on?'],
                [ciSuggestions, setCiSuggestions, 'Any issues or suggestions you would like to mention?'],
              ].map(([val, setter, placeholder], i) => (
                <div key={i}>
                  <label style={lbl}>{placeholder}</label>
                  <textarea value={val} onChange={e => setter(e.target.value)} rows={2}
                    style={{ ...inp, resize:'vertical', fontFamily:'inherit', lineHeight:1.5 }}/>
                </div>
              ))}
            </div>
          </div>

          {/* Evaluation Areas */}
          {sections.map(section => (
            <div key={section.key}>
              <div style={sectionHeaderStyle}>⭐ {section.label}</div>
              <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
                {section.items.map(item => {
                  const rating = ratings[item.key] || {};
                  return (
                    <div key={item.key} style={{ background:C.cream, borderRadius:'10px', padding:'12px 14px', border:`1px solid ${C.border}` }}>
                      <div style={{ fontWeight:600, fontSize:'13px', color:C.charcoal, marginBottom:'8px' }}>{item.label}</div>
                      <div style={{ display:'flex', gap:'6px', marginBottom:'8px' }}>
                        {[1,2,3,4].map(s => (
                          <ScoreButton key={s} score={s} selected={rating.score} onSelect={score => setScore(item.key, score)}/>
                        ))}
                      </div>
                      <input
                        value={rating.notes || ''}
                        onChange={e => setNotes(item.key, e.target.value)}
                        placeholder="Notes (optional)"
                        style={{ ...inp, fontSize:'12px', background:C.white }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Ambitions */}
          <div>
            <div style={sectionHeaderStyle}>🚀 Ambitions</div>
            <label style={lbl}>Where do you see yourself in 6–12 months?</label>
            <textarea value={ambitions} onChange={e => setAmbitions(e.target.value)} rows={3}
              style={{ ...inp, resize:'vertical', fontFamily:'inherit', lineHeight:1.5 }}
              placeholder="Employee's own words…"/>
          </div>

          {/* Goals */}
          <div>
            <div style={sectionHeaderStyle}>🎯 Goals</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
              {goals.map((g, i) => (
                <div key={i} style={{ background:C.cream, borderRadius:'10px', padding:'12px 14px', border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', marginBottom:'8px' }}>Goal {i + 1}</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                    <input value={g.title} onChange={e => setGoalField(i, 'title', e.target.value)} placeholder="Goal title" style={inp}/>
                    <input value={g.action} onChange={e => setGoalField(i, 'action', e.target.value)} placeholder="Action / how to achieve it" style={inp}/>
                    <div>
                      <label style={{ ...lbl, fontWeight:400, color:C.mid }}>Target date</label>
                      <input type="date" value={g.target_date} onChange={e => setGoalField(i, 'target_date', e.target.value)} style={inp}/>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Signatures */}
          <div>
            <div style={sectionHeaderStyle}>✍️ Signatures</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {[
                [signedEmployee, setSignedEmployee, `${member.name} has reviewed and acknowledged this evaluation`],
                [signedManager,  setSignedManager,  `${currentUser?.name || 'Manager'} confirms this evaluation is accurate`],
              ].map(([val, setter, label], i) => (
                <div key={i} onClick={() => setter(!val)}
                  style={{ display:'flex', alignItems:'center', gap:'10px', padding:'12px 14px', background:val?C.greenLight:C.white, border:`1px solid ${val?C.green+'55':C.border}`, borderRadius:'8px', cursor:'pointer', transition:'all 0.15s' }}>
                  {val ? <CheckCircle size={18} color={C.green}/> : <Circle size={18} color={C.border}/>}
                  <span style={{ fontSize:'13px', color:val?C.green:C.charcoal, fontWeight:val?600:400 }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Score summary + save */}
          {!allScoresFilled() && (
            <div style={{ padding:'10px 14px', background:C.amberLight, border:`1px solid ${C.amber}44`, borderRadius:'8px', fontSize:'12px', color:C.amber, fontWeight:600 }}>
              ⚠ {totalItems - scoredItems} area{totalItems - scoredItems !== 1 ? 's' : ''} still need a score before you can save.
            </div>
          )}

          <div style={{ display:'flex', gap:'10px' }}>
            <button onClick={handleSave} disabled={saving || !allScoresFilled()}
              style={{ flex:1, padding:'12px', background:saving||!allScoresFilled()?C.border:C.navy, color:C.white, border:'none', borderRadius:'9px', cursor:saving||!allScoresFilled()?'not-allowed':'pointer', fontWeight:700, fontSize:'14px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
              {saving ? 'Saving…' : '💾 Save Evaluation'}
            </button>
            <button onClick={onClose}
              style={{ padding:'12px 18px', border:`1px solid ${C.border}`, borderRadius:'9px', background:C.white, cursor:'pointer', fontSize:'13px', color:C.mid }}>
              Cancel
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// EVALUATION HISTORY MODAL
// ══════════════════════════════════════════════════════════════════════
export function EvaluationHistoryModal({ member, onClose, onNew }) {
  const [evals,    setEvals]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('evaluations')
        .select('*')
        .eq('team_member_id', member.id)
        .order('date', { ascending: false });
      setEvals(data || []);
      setLoading(false);
    }
    load();
  }, [member.id]);

  function avgScore(ratings) {
    if (!ratings) return null;
    const vals = Object.values(ratings).map(r => r.score).filter(Boolean);
    if (!vals.length) return null;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  }

  function scoreColor(avg) {
    if (!avg) return C.mid;
    if (avg >= 3.5) return C.green;
    if (avg >= 2.5) return C.skyText;
    if (avg >= 1.5) return C.amber;
    return C.red;
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:700, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'620px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', display:'flex', flexDirection:'column' }}>

        <div style={{ padding:'18px 24px', background:C.navy, borderRadius:'16px 16px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:10 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'15px', color:C.white }}>Evaluation History</div>
            <div style={{ fontSize:'12px', color:C.skyBlue, marginTop:'2px' }}>{member.name}</div>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={onNew}
              style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 14px', background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:'8px', cursor:'pointer', color:C.white, fontSize:'12px', fontWeight:600 }}>
              <Plus size={13}/>New Evaluation
            </button>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex' }}><X size={18}/></button>
          </div>
        </div>

        <div style={{ padding:'20px 24px' }}>
          {loading ? (
            <p style={{ color:C.mid, fontSize:'13px' }}>Loading…</p>
          ) : evals.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px 0' }}>
              <div style={{ fontSize:'2.5rem', marginBottom:'12px' }}>📋</div>
              <p style={{ fontWeight:700, fontSize:'15px', color:C.charcoal, margin:'0 0 6px' }}>No evaluations yet</p>
              <p style={{ fontSize:'13px', color:C.mid, margin:'0 0 20px' }}>Run the first probation evaluation for {member.name}.</p>
              <button onClick={onNew}
                style={{ padding:'10px 24px', background:C.navy, color:C.white, border:'none', borderRadius:'9px', cursor:'pointer', fontWeight:600, fontSize:'13px' }}>
                Start Evaluation
              </button>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              {evals.map(ev => {
                const avg = avgScore(ev.ratings);
                const isOpen = expanded === ev.id;
                const sections = EVAL_SECTIONS[ev.type] || [];
                return (
                  <div key={ev.id} style={{ border:`1px solid ${C.border}`, borderRadius:'12px', overflow:'hidden' }}>
                    {/* Summary row */}
                    <button onClick={() => setExpanded(isOpen ? null : ev.id)}
                      style={{ width:'100%', display:'flex', alignItems:'center', gap:'12px', padding:'14px 16px', background:isOpen?C.cream:C.white, border:'none', cursor:'pointer', textAlign:'left' }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'3px' }}>
                          <span style={{ fontWeight:700, fontSize:'13px', color:C.charcoal }}>{fmt(ev.date)}</span>
                          <span style={{ padding:'2px 8px', borderRadius:'20px', fontSize:'11px', fontWeight:700, background:ev.type==='FOH'?C.skyBlueLight:C.orangeLight, color:ev.type==='FOH'?C.skyText:C.amber }}>{ev.type}</span>
                          {ev.eval_type === 'mid_contract' && <span style={{ padding:'2px 8px', borderRadius:'20px', fontSize:'11px', fontWeight:700, background:C.purpleLight, color:C.purple }}>Mid-Contract</span>}
                          {ev.signed_employee && ev.signed_manager && <span style={{ fontSize:'10px', color:C.green, fontWeight:700 }}>✓ Signed</span>}
                        </div>
                        <div style={{ fontSize:'11px', color:C.mid }}>
                          By {ev.evaluator}{ev.next_eval_date ? ` · Next: ${fmt(ev.next_eval_date)}` : ''}
                        </div>
                      </div>
                      {avg && (
                        <div style={{ textAlign:'center', flexShrink:0 }}>
                          <div style={{ fontSize:'20px', fontWeight:800, color:scoreColor(avg) }}>{avg}</div>
                          <div style={{ fontSize:'10px', color:C.mid }}>avg score</div>
                        </div>
                      )}
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink:0, transform:isOpen?'rotate(180deg)':'rotate(0)', transition:'transform 0.2s' }}>
                        <path d="M2.5 5L7 9.5L11.5 5" stroke={C.mid} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>

                    {/* Expanded detail */}
                    {isOpen && (
                      <div style={{ padding:'16px', borderTop:`1px solid ${C.border}`, background:C.cream, display:'flex', flexDirection:'column', gap:'16px' }}>

                        {/* Check-in summary */}
                        {(ev.ci_going || ev.ci_doing_well || ev.ci_improve || ev.ci_suggestions) && (
                          <div>
                            <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'8px' }}>Check-in</div>
                            <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                              {[['How things are going', ev.ci_going], ['Doing well', ev.ci_doing_well], ['Improve', ev.ci_improve], ['Suggestions', ev.ci_suggestions]].filter(([,v])=>v).map(([l,v])=>(
                                <div key={l} style={{ background:C.white, borderRadius:'8px', padding:'10px 12px', border:`1px solid ${C.border}` }}>
                                  <div style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', marginBottom:'3px' }}>{l}</div>
                                  <div style={{ fontSize:'13px', color:C.charcoal, lineHeight:1.5 }}>{v}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Scores */}
                        <div>
                          <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'8px' }}>Scores</div>
                          <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                            {sections.map(section => (
                              <div key={section.key}>
                                <div style={{ fontSize:'11px', fontWeight:700, color:C.navy, marginBottom:'5px' }}>{section.label}</div>
                                <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                                  {section.items.map(item => {
                                    const r = ev.ratings?.[item.key];
                                    const s = r?.score ? SCORE_LABELS[r.score] : null;
                                    return (
                                      <div key={item.key} style={{ display:'flex', alignItems:'flex-start', gap:'8px', padding:'6px 10px', background:C.white, borderRadius:'6px', border:`1px solid ${C.border}` }}>
                                        <span style={{ flex:1, fontSize:'12px', color:C.charcoal }}>{item.label}</span>
                                        {s ? (
                                          <span style={{ padding:'2px 8px', borderRadius:'20px', fontSize:'11px', fontWeight:700, background:s.bg, color:s.color, whiteSpace:'nowrap', flexShrink:0 }}>
                                            {r.score} — {s.label}
                                          </span>
                                        ) : (
                                          <span style={{ fontSize:'11px', color:C.border }}>—</span>
                                        )}
                                        {r?.notes && <div style={{ width:'100%', fontSize:'11px', color:C.mid, fontStyle:'italic', marginTop:'2px', paddingTop:'4px', borderTop:`1px solid ${C.border}` }}>{r.notes}</div>}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Ambitions */}
                        {ev.ambitions && (
                          <div>
                            <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'6px' }}>Ambitions</div>
                            <div style={{ background:C.white, borderRadius:'8px', padding:'10px 12px', border:`1px solid ${C.border}`, fontSize:'13px', color:C.charcoal, lineHeight:1.5 }}>{ev.ambitions}</div>
                          </div>
                        )}

                        {/* Goals */}
                        {ev.goals?.length > 0 && (
                          <div>
                            <div style={{ fontSize:'11px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'6px' }}>Goals</div>
                            <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                              {ev.goals.map((g, i) => (
                                <div key={i} style={{ background:C.white, borderRadius:'8px', padding:'10px 12px', border:`1px solid ${C.border}` }}>
                                  <div style={{ fontWeight:600, fontSize:'13px', color:C.charcoal }}>{i+1}. {g.title}</div>
                                  {g.action && <div style={{ fontSize:'12px', color:C.mid, marginTop:'2px' }}>{g.action}</div>}
                                  {g.target_date && <div style={{ fontSize:'11px', color:C.skyText, marginTop:'3px' }}>Target: {fmt(g.target_date)}</div>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Signatures */}
                        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                          {[['Employee', ev.signed_employee], ['Manager', ev.signed_manager]].map(([label, signed]) => (
                            <span key={label} style={{ padding:'4px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:700, background:signed?C.greenLight:C.redLight, color:signed?C.green:C.red }}>
                              {label}: {signed ? '✓ Signed' : '✗ Not signed'}
                            </span>
                          ))}
                          {ev.email_sent_at && <span style={{ padding:'4px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:600, background:C.skyBlueLight, color:C.skyText }}>📧 Emailed {fmt(ev.email_sent_at)}</span>}
                        </div>

                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}